import { useEffect, useState, useCallback } from 'react';
import {
  getUsuarios,
  getUsuario,
  getHistorialUsuario,
  actualizarUsuario,
  deleteUsuario,
  reenviarCorreoVerificacion,
  crearAfiliacionAdmin,
  asociarUsuarioAClubAdmin,
  quitarMembresiaClubAdmin,
  banearUsuario,
  enviarCartaBaneo,
  verCartaBaneoPdf
} from '../../../services/adminService';
import { getClubs } from '../../../services/clubsService';
import { useToast } from '../context/ToastContext';
import AdminSpinner from '../components/AdminSpinner';
import { formatFechaLarga, parseFechaLocal } from '../../../utils/fechas';

const MODALIDAD_RANKING = { raw: 'Raw', classic_raw: 'Classic Raw', equipado: 'Equipado', single_ply: 'Single Ply', bench: 'Solo banca', deadlift: 'Solo peso muerto' };
const capitalizar = (t) => (t ? String(t).charAt(0).toUpperCase() + String(t).slice(1) : '');
const formatoNum = (n) => (n == null ? '—' : Number(n).toLocaleString('es-CL', { maximumFractionDigits: 2 }));

/* Foto de perfil del usuario o, si no tiene, sus iniciales */
const AvatarUsuario = ({ usuario, size = 72 }) => {
  const [rota, setRota] = useState(false);
  useEffect(() => { setRota(false); }, [usuario?.foto_perfil_url]);
  const iniciales = `${(usuario?.nombre || usuario?.email || '?').charAt(0)}${(usuario?.apellido || '').charAt(0)}`.toUpperCase();
  const base = {
    width: size, height: size, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#1a1a1a', color: '#fff', fontWeight: 700, fontSize: size * 0.36, letterSpacing: '1px',
    border: '3px solid #e8e8e8',
  };
  if (usuario?.foto_perfil_url && !rota) {
    return (
      <a href={usuario.foto_perfil_url} target="_blank" rel="noreferrer" title="Ver foto en tamaño completo" style={base}>
        <img src={usuario.foto_perfil_url} alt={`Foto de ${usuario.nombre || 'usuario'}`} onError={() => setRota(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </a>
    );
  }
  return <div style={base} aria-label="Sin foto de perfil">{iniciales}</div>;
};

const TIPOS_PERFIL = [
  { value: 'atleta', label: 'Atleta' },
  { value: 'meet_director', label: 'Meet director' },
  { value: 'presidente_club', label: 'Presidente club' },
  { value: 'admin', label: 'Admin' }
];

/* Colores por tipo de perfil (badges) */
const perfilColor = {
  admin: { bg: '#e8eaf6', color: '#283593', border: '#3f51b5' },
  atleta: { bg: '#e8f5e9', color: '#2e7d32', border: '#43a047' },
  meet_director: { bg: '#f3e5f5', color: '#6a1b9a', border: '#8e24aa' },
  presidente_club: { bg: '#fff3e0', color: '#e65100', border: '#f57c00' },
};
const perfilColorDefault = { bg: '#eceff1', color: '#455a64', border: '#78909c' };

const getBadgePerfilStyle = (tipo) => {
  const c = perfilColor[tipo] || perfilColorDefault;
  return {
    background: c.bg,
    color: c.color,
    border: `1px solid ${c.border}`,
    padding: '5px 11px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'capitalize',
    display: 'inline-flex',
    alignItems: 'center',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
    whiteSpace: 'nowrap',
  };
};

/* =========================
   CONFIG VISUAL
========================= */
const estadoColor = {
  activo: '#2e7d32',
  inactivo: '#616161',
  baneado: '#c62828',
  suspendido: '#ef6c00',
};

const styles = {
  container: { padding: '24px', maxWidth: '1200px' },
  header: { marginBottom: '20px' },
  title: { margin: 0, fontSize: '28px', fontWeight: 700 },
  subtitle: { color: '#666', marginTop: '6px' },

  filters: { display: 'flex', gap: '12px', marginBottom: '18px', flexWrap: 'wrap' },

  input: {
    padding: '10px 12px',
    width: '280px',
    border: '1px solid #ddd',
    borderRadius: '8px'
  },

  select: {
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid #ddd'
  },

  btnPrimary: {
    padding: '10px 16px',
    background: '#1a4d8c',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },

  btnGhost: {
    padding: '10px 14px',
    background: '#f4f6f8',
    color: '#111',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },

  iconBtn: {
    padding: '8px 12px',
    background: '#f4f6f8',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
    marginRight: '6px'
  },

  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    padding: '12px'
  },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px', borderBottom: '1px solid #e0e0e0', textAlign: 'left' },
  td: { padding: '12px', borderBottom: '1px solid #f0f0f0' },

  perfilesCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
    minHeight: '28px',
  },

  badgeEstado: {
    color: '#fff',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'capitalize',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },

  // Paginación
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
    padding: '12px 0',
    borderTop: '1px solid #eee'
  },

  paginationInfo: {
    color: '#666',
    fontSize: 14
  },

  paginationButtons: {
    display: 'flex',
    gap: 8
  },

  btnPagination: {
    background: '#fff',
    color: '#1a4d8c',
    border: '1px solid #1a4d8c',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500
  },

  btnPaginationDisabled: {
    background: '#f5f5f5',
    color: '#999',
    border: '1px solid #ddd',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'not-allowed',
    fontSize: '13px'
  },

  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },

  modal: {
    background: '#fff',
    borderRadius: '14px',
    padding: '20px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column'
  },

  modalBody: {
    overflowY: 'auto',
    flex: '1 1 auto'
  },

  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '20px',
    flexShrink: 0,
    paddingTop: '16px',
    borderTop: '1px solid #eee'
  }
};

/* =========================
   COMPONENTE
========================= */
const UsuariosView = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });

  const [usuarioVer, setUsuarioVer] = useState(null);
  const [usuarioConfig, setUsuarioConfig] = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [perfilesSeleccionados, setPerfilesSeleccionados] = useState([]);
  const [usuarioEditDatos, setUsuarioEditDatos] = useState(null);
  const [editDatosForm, setEditDatosForm] = useState({});
  const [afiliando, setAfiliando] = useState(false);

  const [clubesDisponibles, setClubesDisponibles] = useState([]);
  const [clubSeleccionadoId, setClubSeleccionadoId] = useState('');
  const [accionClubLoading, setAccionClubLoading] = useState(false);

  const [banModalOpen, setBanModalOpen] = useState(false);
  const [banMotivo, setBanMotivo] = useState('');
  const [banPermanente, setBanPermanente] = useState(false);
  const [banFechaFin, setBanFechaFin] = useState('');
  const [banSubmitting, setBanSubmitting] = useState(false);
  const [enviandoCarta, setEnviandoCarta] = useState(false);
  const [abriendoCarta, setAbriendoCarta] = useState(false);
  const [historial, setHistorial] = useState(null);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [errorHistorial, setErrorHistorial] = useState('');

  // Historial deportivo (ranking vigente y últimos eventos) al abrir la ficha
  const rutVer = usuarioVer?.rut;
  useEffect(() => {
    setHistorial(null);
    setErrorHistorial('');
    if (!rutVer) return undefined;
    let vigente = true;
    setCargandoHistorial(true);
    getHistorialUsuario(rutVer)
      .then((d) => { if (vigente) setHistorial(d); })
      .catch((e) => { if (vigente) setErrorHistorial(e.message || 'No se pudo cargar el historial'); })
      .finally(() => { if (vigente) setCargandoHistorial(false); });
    return () => { vigente = false; };
  }, [rutVer]);

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUsuarios({ page, perPage: 10, search, estado });
      setUsuarios(data.items || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        total_pages: 1,
        has_next: false,
        has_prev: false
      });
      return data;
    } finally {
      setLoading(false);
    }
  }, [page, search, estado]);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  useEffect(() => {
    if (!usuarioVer) setBanModalOpen(false);
  }, [usuarioVer]);

  // Cargar clubes disponibles para el selector del admin (solo cuando se abre el modal de usuario)
  useEffect(() => {
    let cancelled = false;
    const cargarClubs = async () => {
      try {
        const data = await getClubs(1, 1000, '');
        if (cancelled) return;
        setClubesDisponibles(data.items || []);
      } catch (e) {
        // No bloquea el resto de funcionalidades
        console.warn('Error cargando clubes para selector:', e?.message || e);
      }
    };

    if (usuarioVer && clubesDisponibles.length === 0) {
      cargarClubs();
    }

    return () => {
      cancelled = true;
    };
  }, [usuarioVer, clubesDisponibles.length]);

  // Mantener sincronizado el club seleccionado según el club activo del usuario
  useEffect(() => {
    if (!usuarioVer) return;
    const idActivo = Array.isArray(usuarioVer.clubes) && usuarioVer.clubes.length
      ? usuarioVer.clubes[0]?.id
      : '';
    setClubSeleccionadoId(idActivo !== '' && idActivo != null ? String(idActivo) : '');
  }, [usuarioVer]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      setPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagination.has_prev) {
      setPage(prev => prev - 1);
    }
  };

  const togglePerfil = (tipo) => {
    setPerfilesSeleccionados(prev =>
      prev.includes(tipo) ? prev.filter(p => p !== tipo) : [...prev, tipo]
    );
  };

  const defaultFechaFinPreset = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  };

  const abrirModalBanear = () => {
    setBanMotivo('');
    setBanPermanente(false);
    setBanFechaFin(defaultFechaFinPreset());
    setBanModalOpen(true);
  };

  const aplicarDiasBan = (dias) => {
    const d = new Date();
    d.setDate(d.getDate() + dias);
    setBanFechaFin(d.toISOString().slice(0, 10));
  };

  /** Misma fecha de calendario local (hoy + N años). */
  const aplicarAniosBan = (anios) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + anios);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    setBanFechaFin(`${y}-${m}-${day}`);
  };

  const descargarPdfBase64 = (base64, filename) => {
    const byteChars = atob(base64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i += 1) {
      bytes[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'carta_baneo.pdf';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleConfirmarBanear = async () => {
    if (!usuarioVer) return;
    const motivo = (banMotivo || '').trim();
    if (motivo.length < 3) {
      showToast('Escribe el motivo del baneo (mínimo 3 caracteres)', 'error');
      return;
    }
    if (!banPermanente) {
      if (!banFechaFin) {
        showToast('Indica la fecha de término del ban temporal', 'error');
        return;
      }
    }
    setBanSubmitting(true);
    try {
      const payload = {
        motivo,
        ban_permanente: banPermanente,
        ...(banPermanente ? {} : { fecha_fin: banFechaFin })
      };
      const res = await banearUsuario(usuarioVer.rut, payload);
      const d = res?.data;
      if (d?.pdf_base64 && d?.filename) {
        descargarPdfBase64(d.pdf_base64, d.filename);
      }
      showToast('Usuario baneado. Carta descargada. Puedes enviarla por correo con «Enviar carta».', 'success');
      setBanModalOpen(false);
      const actualizado = await getUsuario(usuarioVer.rut);
      setUsuarioVer(actualizado);
      fetchUsuarios();
    } catch (e) {
      showToast(e.message || 'Error al banear', 'error');
    } finally {
      setBanSubmitting(false);
    }
  };

  const handleEnviarCartaBaneo = async () => {
    if (!usuarioVer) return;
    setEnviandoCarta(true);
    try {
      await enviarCartaBaneo(usuarioVer.rut);
      showToast('Carta enviada por correo al atleta.', 'success');
    } catch (e) {
      showToast(e.message || 'Error al enviar la carta', 'error');
    } finally {
      setEnviandoCarta(false);
    }
  };

  const handleVerCartaBaneo = async () => {
    if (!usuarioVer) return;
    setAbriendoCarta(true);
    try {
      await verCartaBaneoPdf(usuarioVer.rut);
    } catch (e) {
      showToast(e.message || 'Error al abrir la carta', 'error');
    } finally {
      setAbriendoCarta(false);
    }
  };

  const handleBorrar = async () => {
    if (!usuarioVer) return;
    const nombre = [usuarioVer.nombre, usuarioVer.apellido].filter(Boolean).join(' ') || usuarioVer.email || usuarioVer.rut;
    if (!window.confirm(`¿Borrar al usuario ${nombre}? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteUsuario(usuarioVer.rut);
      showToast('Usuario eliminado', 'success');
      setUsuarioVer(null);
      fetchUsuarios();
    } catch (e) {
      showToast(e.message || 'Error al borrar', 'error');
    }
  };

  const handleAfiliar = async () => {
    if (!usuarioVer) return;
    setAfiliando(true);
    try {
      await crearAfiliacionAdmin(usuarioVer.rut, 'anual');
      showToast('Afiliación creada. Se envió un correo al usuario con el número de afiliación.', 'success');
      const data = await getUsuario(usuarioVer.rut);
      setUsuarioVer(data);
      fetchUsuarios();
    } catch (e) {
      showToast(e.message || 'Error al afiliar', 'error');
    } finally {
      setAfiliando(false);
    }
  };

  const handleAsociarClubAdmin = async () => {
    if (!usuarioVer) return;
    if (!clubSeleccionadoId) {
      showToast('Selecciona un club', 'error');
      return;
    }

    setAccionClubLoading(true);
    try {
      await asociarUsuarioAClubAdmin(usuarioVer.rut, clubSeleccionadoId);
      showToast('Membresía actualizada', 'success');
      const data = await getUsuario(usuarioVer.rut);
      setUsuarioVer(data);
      fetchUsuarios();
    } catch (e) {
      showToast(e.message || 'Error al asociar', 'error');
    } finally {
      setAccionClubLoading(false);
    }
  };

  const handleQuitarClubAdmin = async () => {
    if (!usuarioVer) return;

    setAccionClubLoading(true);
    try {
      await quitarMembresiaClubAdmin(usuarioVer.rut);
      showToast('Membresía removida', 'success');
      const data = await getUsuario(usuarioVer.rut);
      setUsuarioVer(data);
      fetchUsuarios();
    } catch (e) {
      showToast(e.message || 'Error al quitar membresía', 'error');
    } finally {
      setAccionClubLoading(false);
    }
  };

  const handleReenviarCorreo = async () => {
    if (!usuarioVer) return;
    try {
      await reenviarCorreoVerificacion(usuarioVer.rut);
      showToast('Correo de verificación enviado', 'success');
    } catch (e) {
      showToast(e.message || 'Error al reenviar correo', 'error');
    }
  };

  const abrirEditarDatos = (u) => {
    setUsuarioEditDatos(u);
    setEditDatosForm({
      nombre: u.nombre || '',
      apellido: u.apellido || '',
      email: u.email || '',
      telefono: u.telefono || '',
      fecha_nacimiento: u.fecha_nacimiento ? (u.fecha_nacimiento.slice ? u.fecha_nacimiento.slice(0, 10) : u.fecha_nacimiento) : '',
      genero: u.genero || '',
      ciudad: u.ciudad || '',
      region: u.region || '',
    });
  };

  const guardarEdicionDatos = async () => {
    if (!usuarioEditDatos) return;
    try {
      await actualizarUsuario(usuarioEditDatos.rut, {
        nombre: editDatosForm.nombre,
        apellido: editDatosForm.apellido,
        email: editDatosForm.email || undefined,
        telefono: editDatosForm.telefono || undefined,
        fecha_nacimiento: editDatosForm.fecha_nacimiento || undefined,
        genero: editDatosForm.genero || undefined,
        ciudad: editDatosForm.ciudad || undefined,
        region: editDatosForm.region || undefined,
      });
      showToast('Datos actualizados correctamente', 'success');
      const data = await getUsuario(usuarioEditDatos.rut);
      setUsuarioVer(prev => (prev?.rut === data.rut ? data : prev));
      setUsuarioEditDatos(null);
      fetchUsuarios();
    } catch (err) {
      showToast(err?.message || 'Error al guardar', 'error');
    }
  };

  const guardarEdicion = async () => {
    const rutEditado = usuarioConfig.rut;
    try {
      const res = await actualizarUsuario(usuarioConfig.rut, {
        estado: nuevoEstado,
        perfiles: perfilesSeleccionados
      });
      setUsuarioConfig(null);

      if (res?.data?.rut) {
        const { estado: estadoResp, perfiles: perfilesResp } = res.data;
        setUsuarios(prev =>
          prev.map(u =>
            u.rut === rutEditado
              ? { ...u, estado: estadoResp ?? u.estado, perfiles: Array.isArray(perfilesResp) ? perfilesResp : (u.perfiles || []) }
              : u
          )
        );
        if (usuarioVer?.rut === rutEditado) {
          setUsuarioVer(prev => prev ? { ...prev, estado: estadoResp ?? prev.estado, perfiles: Array.isArray(perfilesResp) ? perfilesResp : (prev.perfiles || []) } : null);
        }
      } else {
        const data = await fetchUsuarios();
        if (usuarioVer?.rut === rutEditado && data?.items?.length) {
          const actualizado = data.items.find(u => u.rut === rutEditado);
          if (actualizado) setUsuarioVer(actualizado);
        }
      }
      showToast('Usuario actualizado correctamente', 'success');
    } catch (err) {
      showToast(err?.message || 'Error al guardar', 'error');
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Usuarios</h1>
          <p style={styles.subtitle}>Administración de usuarios del sistema</p>
        </div>
        <AdminSpinner label="Cargando usuarios..." />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* HEADER */}
      <div style={styles.header}>
        <h1 style={styles.title}>Usuarios</h1>
        <p style={styles.subtitle}>Administración de usuarios del sistema</p>
      </div>

      {/* FILTROS */}
      <form onSubmit={handleSearch} style={styles.filters}>
        <input
          placeholder="Buscar por RUT, email, nombre o N° de afiliación"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          style={{ ...styles.input, minWidth: 'min(360px, 100%)' }}
        />

        <select
          value={estado}
          onChange={e => {
            setEstado(e.target.value);
            setPage(1);
          }}
          style={styles.select}
        >
          <option value="">Todos</option>
          <option value="activo">Activo</option>
          <option value="inactivo">Inactivo</option>
          <option value="baneado">Baneado</option>
          <option value="suspendido">Suspendido</option>
        </select>

        <button type="submit" style={styles.btnPrimary}>
          Buscar
        </button>
        {search && (
          <button type="button" style={styles.btnGhost} onClick={handleClearSearch}>
            Limpiar
          </button>
        )}
      </form>

      {/* TABLA */}
      <div style={styles.card}>
        <div className="admin-table-scroll">
          <table style={styles.table}>
            <thead>
            <tr>
              <th style={styles.th}>RUT</th>
              <th style={styles.th}>Nombre</th>
              <th style={styles.th}>Club</th>
              <th style={styles.th}>Estado del email</th>
              <th style={styles.th}>Perfiles</th>
              <th style={styles.th}>Estado</th>
              <th style={styles.th}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map(u => {
              const clubesDisplay = (u.clubes && u.clubes.length) ? u.clubes.map(c => c.nombre).join(', ') : null;
              return (
              <tr key={u.rut}>
                <td style={styles.td}>{u.rut}</td>
                <td style={styles.td}>
                  {u.nombre} {u.apellido}
                  {u.numero_afiliacion && (
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>Afiliación Nº {u.numero_afiliacion}</div>
                  )}
                </td>
                <td style={styles.td}>{clubesDisplay || '—'}</td>
                <td style={styles.td}>{u.email_verificado ? 'Verificado' : 'No verificado'}</td>
                <td style={styles.td}>
                  <div style={styles.perfilesCell}>
                    {u.perfiles?.length
                      ? u.perfiles.map(p => (
                          <span key={p} style={getBadgePerfilStyle(p)}>{p.replace(/_/g, ' ')}</span>
                        ))
                      : <span style={{ color: '#9e9e9e', fontSize: '13px' }}>—</span>}
                  </div>
                </td>
                <td style={styles.td}>
                  <span
                    style={{
                      ...styles.badgeEstado,
                      background: estadoColor[u.estado]
                    }}
                  >
                    {u.estado}
                  </span>
                </td>
                <td style={styles.td}>
                  <button
                    style={styles.iconBtn}
                    onClick={async () => {
                      try {
                        const data = await getUsuario(u.rut);
                        setUsuarioVer(data);
                      } catch (err) {
                        showToast(err.message || 'Error al cargar usuario', 'error');
                      }
                    }}
                  >
                    Opciones
                  </button>

                  <button
                    style={styles.iconBtn}
                    onClick={() => {
                      setUsuarioConfig(u);
                      setNuevoEstado(u.estado);
                      setPerfilesSeleccionados(Array.isArray(u.perfiles) ? [...u.perfiles] : []);
                    }}
                  >
                    Perfil
                  </button>
                </td>
              </tr>
            );
            })}
          </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div style={styles.pagination}>
          <span style={styles.paginationInfo}>
            Mostrando {usuarios.length} de {pagination.total} usuarios
            {pagination.total_pages > 1 && ` (Página ${pagination.page} de ${pagination.total_pages})`}
          </span>
          
          <div style={styles.paginationButtons}>
            <button
              style={pagination.has_prev ? styles.btnPagination : styles.btnPaginationDisabled}
              onClick={handlePrevPage}
              disabled={!pagination.has_prev}
            >
              ← Anterior
            </button>
            <button
              style={pagination.has_next ? styles.btnPagination : styles.btnPaginationDisabled}
              onClick={handleNextPage}
              disabled={!pagination.has_next}
            >
              Siguiente →
            </button>
          </div>
        </div>
      </div>

      {/* MODAL VER USUARIO */}
      {usuarioVer && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, width: 'min(560px, 94vw)' }}>
            <div style={styles.modalBody}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '10px' }}>
                <AvatarUsuario usuario={usuarioVer} />
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ margin: 0 }}>
                    {usuarioVer.nombre} {usuarioVer.apellido}
                  </h2>
                  <div style={{ color: '#666', marginTop: '4px' }}>
                    RUT: {usuarioVer.rut}
                  </div>
                </div>
              </div>

              <span
                style={{
                  ...styles.badgeEstado,
                  background: estadoColor[usuarioVer.estado]
                }}
              >
                {usuarioVer.estado}
              </span>

              <div
                style={{
                  marginTop: '16px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }}
              >
                <div><b>Email</b><div>{usuarioVer.email || '—'}</div></div>
                {(usuarioVer.clubes && usuarioVer.clubes.length > 0) && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <b>Club(s)</b>
                    <div>{usuarioVer.clubes.map(c => c.nombre).join(', ')}</div>
                  </div>
                )}
                <div><b>Teléfono</b><div>{usuarioVer.telefono || '—'}</div></div>
                <div><b>Género</b><div>{usuarioVer.genero || '—'}</div></div>
                <div>
                  <b>Fecha nacimiento</b>
                  <div>
                    {usuarioVer.fecha_nacimiento
                      ? (parseFechaLocal(usuarioVer.fecha_nacimiento) || new Date(usuarioVer.fecha_nacimiento)).toLocaleDateString('es-CL')
                      : '—'}
                  </div>
                </div>
                <div><b>Ciudad</b><div>{usuarioVer.ciudad || '—'}</div></div>
                <div><b>Región</b><div>{usuarioVer.region || '—'}</div></div>
              </div>

              {usuarioVer.estado === 'baneado' && usuarioVer.baneo_activo && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '14px 16px',
                    background: '#fff8e1',
                    borderRadius: '10px',
                    border: '1px solid #ffcc80',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '10px', color: '#e65100' }}>
                    Baneo activo
                  </div>
                  <div style={{ fontSize: '14px', color: '#444', display: 'grid', gap: '8px' }}>
                    <div>
                      <b>Tipo:</b>{' '}
                      {usuarioVer.baneo_activo.tipo_sancion === 'ban_permanente'
                        ? 'Permanente'
                        : usuarioVer.baneo_activo.tipo_sancion === 'ban_temporal'
                          ? 'Temporal'
                          : usuarioVer.baneo_activo.tipo_sancion}
                    </div>
                    <div>
                      <b>Inicio:</b>{' '}
                      {usuarioVer.baneo_activo.fecha_inicio
                        ? new Date(usuarioVer.baneo_activo.fecha_inicio).toLocaleString('es-CL')
                        : '—'}
                    </div>
                    <div>
                      <b>Término:</b>{' '}
                      {usuarioVer.baneo_activo.tipo_sancion === 'ban_permanente'
                        ? '—'
                        : usuarioVer.baneo_activo.fecha_fin
                          ? new Date(usuarioVer.baneo_activo.fecha_fin).toLocaleString('es-CL')
                          : '—'}
                    </div>
                    <div>
                      <b>Motivo:</b>
                      <div
                        style={{
                          marginTop: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {usuarioVer.baneo_activo.motivo || '—'}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
                    <button
                      type="button"
                      style={{ ...styles.btnGhost, padding: '8px 14px' }}
                      onClick={handleVerCartaBaneo}
                      disabled={abriendoCarta}
                      title="Ver la carta en PDF en una nueva pestaña"
                    >
                      {abriendoCarta ? 'Abriendo...' : 'Ver carta'}
                    </button>
                    <button
                      type="button"
                      style={{ ...styles.btnPrimary, padding: '8px 14px' }}
                      onClick={handleEnviarCartaBaneo}
                      disabled={enviandoCarta || !usuarioVer.email}
                      title={!usuarioVer.email ? 'El usuario no tiene email' : 'Enviar carta PDF por correo'}
                    >
                      {enviandoCarta ? 'Enviando...' : 'Enviar carta'}
                    </button>
                    {!usuarioVer.email && (
                      <span style={{ fontSize: '13px', color: '#c62828' }}>
                        Sin email registrado
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div style={{
                marginTop: '18px',
                padding: '14px 16px',
                background: '#f8f9fa',
                borderRadius: '10px',
                border: '1px solid #eee',
              }}>
                <div style={{ fontWeight: 600, marginBottom: '10px', color: '#37474f' }}>Perfiles</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {usuarioVer.perfiles?.length
                    ? usuarioVer.perfiles.map(p => (
                        <span key={p} style={getBadgePerfilStyle(p)}>{p.replace(/_/g, ' ')}</span>
                      ))
                    : <span style={{ color: '#9e9e9e', fontSize: '14px' }}>Ninguno asignado</span>}
                </div>
              </div>

              {/* Ranking vigente */}
              <div style={{
                marginTop: '18px',
                padding: '14px 16px',
                background: '#fdf6f6',
                borderRadius: '10px',
                border: '1px solid #f3d6d6',
              }}>
                <div style={{ fontWeight: 600, marginBottom: '10px', color: '#8e1b1b' }}>
                  Ranking {historial?.ranking?.anio || 'vigente'}
                </div>
                {cargandoHistorial ? (
                  <span style={{ color: '#666', fontSize: '14px' }}>Cargando…</span>
                ) : errorHistorial ? (
                  <span style={{ color: '#c62828', fontSize: '14px' }}>{errorHistorial}</span>
                ) : !historial?.ranking?.anio ? (
                  <span style={{ color: '#666', fontSize: '14px' }}>No hay rankings cargados.</span>
                ) : historial.ranking.posiciones.length === 0 ? (
                  <span style={{ color: '#666', fontSize: '14px' }}>Sin resultados en el ranking {historial.ranking.anio}.</span>
                ) : (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {historial.ranking.posiciones.map((p, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '22px', fontWeight: 800, color: '#8e1b1b', minWidth: '48px' }}>{p.puesto}°</span>
                        <div style={{ fontSize: '14px', color: '#333' }}>
                          <div><b>{formatoNum(p.dots)} DOTS</b> · de {p.total_atletas} en el overall</div>
                          <div style={{ color: '#777', fontSize: '13px' }}>
                            {[MODALIDAD_RANKING[String(p.modalidad || '').toLowerCase()] || capitalizar(p.modalidad), capitalizar(p.tipo_competencia), p.genero].filter(Boolean).join(' · ')}
                            {p.meet_nombre && ` — mejor marca en ${p.meet_nombre}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Últimos eventos */}
              <div style={{
                marginTop: '18px',
                padding: '14px 16px',
                background: '#f8f9fa',
                borderRadius: '10px',
                border: '1px solid #eee',
              }}>
                <div style={{ fontWeight: 600, marginBottom: '10px', color: '#37474f' }}>Últimos eventos</div>
                {cargandoHistorial ? (
                  <span style={{ color: '#666', fontSize: '14px' }}>Cargando…</span>
                ) : errorHistorial ? (
                  <span style={{ color: '#c62828', fontSize: '14px' }}>{errorHistorial}</span>
                ) : !historial?.ultimos_eventos?.length ? (
                  <span style={{ color: '#666', fontSize: '14px' }}>Sin participaciones registradas.</span>
                ) : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '10px' }}>
                    {historial.ultimos_eventos.map((e, i) => (
                      <li key={i} style={{ borderLeft: '3px solid #c62828', paddingLeft: '10px' }}>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{e.nombre || 'Evento'}</div>
                        <div style={{ color: '#777', fontSize: '13px' }}>
                          {[e.fecha && formatFechaLarga(e.fecha), e.division, e.categoria_peso && (/kg/i.test(e.categoria_peso) ? e.categoria_peso : `${e.categoria_peso} kg`)].filter(Boolean).join(' · ')}
                        </div>
                        <div style={{ fontSize: '13px', color: '#333' }}>
                          {e.total != null
                            ? <>Total {formatoNum(e.total)} kg · <b>{formatoNum(e.dots)} DOTS</b></>
                            : <span style={{ color: '#888' }}>Sin resultados cargados</span>}
                          {e.estado && <span style={{ color: '#888' }}> · Estado inscripción: {e.estado}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Afiliación */}
              <div style={{
                marginTop: '18px',
                padding: '14px 16px',
                background: '#f0f7ff',
                borderRadius: '10px',
                border: '1px solid #d0e3ff',
              }}>
                <div style={{ fontWeight: 600, marginBottom: '10px', color: '#1a4d8c' }}>Afiliación</div>
                {usuarioVer.afiliaciones?.length
                  ? (
                      <ul style={{ margin: 0, paddingLeft: '18px' }}>
                        {usuarioVer.afiliaciones.map(a => (
                          <li key={a.id} style={{ marginBottom: '6px' }}>
                            <strong>Nº {a.numero_afiliacion}</strong>
                            {a.fecha_fin && ` — vigente hasta ${(parseFechaLocal(a.fecha_fin) || new Date(a.fecha_fin)).toLocaleDateString('es-CL')}`}
                            {a.estado && ` (${a.estado})`}
                          </li>
                        ))}
                      </ul>
                    )
                  : <span style={{ color: '#666', fontSize: '14px' }}>Sin afiliación</span>}
                {(!usuarioVer.afiliaciones || usuarioVer.afiliaciones.length === 0) && (
                  <div style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      style={{ ...styles.iconBtn, background: '#1a4d8c', color: '#fff', borderColor: '#1a4d8c' }}
                      onClick={handleAfiliar}
                      disabled={afiliando}
                    >
                      {afiliando ? 'Afiliando...' : 'Afiliar'}
                    </button>
                  </div>
                )}
              </div>

              {/* Membresía de club (admin: sin restricción de club) */}
              <div style={{
                marginTop: '18px',
                padding: '14px 16px',
                background: '#f8f9fa',
                borderRadius: '10px',
                border: '1px solid #eee',
              }}>
                <div style={{ fontWeight: 600, marginBottom: '10px', color: '#37474f' }}>
                  Membresía de club
                </div>

                <div style={{ color: '#666', marginBottom: '12px', fontSize: '14px' }}>
                  <b>Club activo:</b>{' '}
                  {usuarioVer.clubes?.length ? usuarioVer.clubes.map(c => c.nombre).join(', ') : '—'}
                </div>

                <div style={{ display: 'grid', gap: '10px' }}>
                  <select
                    value={clubSeleccionadoId}
                    onChange={(e) => setClubSeleccionadoId(e.target.value)}
                    style={{ ...styles.select, width: '100%' }}
                    disabled={!clubesDisponibles || clubesDisponibles.length === 0}
                  >
                    <option value="">{clubesDisponibles?.length ? 'Selecciona un club' : 'Cargando clubes...'}</option>
                    {clubesDisponibles?.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.nombre}
                      </option>
                    ))}
                  </select>

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      style={styles.iconBtn}
                      onClick={handleAsociarClubAdmin}
                      disabled={accionClubLoading || !clubSeleccionadoId}
                      title="Asociar usuario a un club"
                    >
                      {accionClubLoading ? 'Asociando...' : 'Asociar'}
                    </button>

                    <button
                      style={styles.iconBtn}
                      onClick={handleQuitarClubAdmin}
                      disabled={accionClubLoading || !(usuarioVer.clubes?.length > 0)}
                      title="Quitar club activo"
                    >
                      {accionClubLoading ? 'Procesando...' : 'Quitar club activo'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.btnGhost}
                onClick={() => setUsuarioVer(null)}
              >
                Cerrar
              </button>
              <button
                style={styles.iconBtn}
                onClick={() => abrirEditarDatos(usuarioVer)}
                title="Editar datos personales"
              >
                Editar datos
              </button>
              <button
                style={{ ...styles.iconBtn, background: '#ffebee', borderColor: '#c62828', color: '#c62828' }}
                onClick={abrirModalBanear}
                disabled={usuarioVer.estado === 'baneado'}
                title="Banear usuario"
              >
                Banear
              </button>
              <button
                style={{ ...styles.iconBtn, background: '#ffebee', borderColor: '#b71c1c', color: '#b71c1c' }}
                onClick={handleBorrar}
                title="Eliminar usuario"
              >
                Borrar
              </button>
              <button
                style={styles.iconBtn}
                onClick={handleReenviarCorreo}
                title="Reenviar correo de verificación"
              >
                Reenviar correo
              </button>
            </div>
          </div>
        </div>
      )}

      {banModalOpen && usuarioVer && (
        <div
          style={{ ...styles.modalOverlay, zIndex: 1100 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !banSubmitting) setBanModalOpen(false);
          }}
        >
          <div
            style={{ ...styles.modal, width: '480px', maxWidth: '95vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Banear usuario</h3>
            <p style={{ color: '#666', marginBottom: '12px', fontSize: '14px' }}>
              {usuarioVer.rut}
              {' — '}
              {[usuarioVer.nombre, usuarioVer.apellido].filter(Boolean).join(' ') || usuarioVer.email || '—'}
            </p>
            <p style={{ color: '#555', fontSize: '13px', marginBottom: '14px' }}>
              Se generará una carta oficial (PDF) desde Achipo hacia el atleta y se descargará en tu navegador.
              El envío por correo lo haces después con el botón «Enviar carta» en el detalle del usuario.
            </p>

            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: '13px', display: 'block', marginBottom: '8px' }}>
                  Tipo de baneo
                </span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '6px' }}>
                  <input
                    type="radio"
                    name="banTipo"
                    checked={!banPermanente}
                    onChange={() => setBanPermanente(false)}
                  />
                  Temporal (hasta una fecha)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="banTipo"
                    checked={banPermanente}
                    onChange={() => setBanPermanente(true)}
                  />
                  Permanente
                </label>
              </div>

              {!banPermanente && (
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px' }}>
                    Fecha de término
                  </label>
                  <input
                    type="date"
                    value={banFechaFin}
                    onChange={(e) => setBanFechaFin(e.target.value)}
                    style={{ ...styles.input, width: '100%', maxWidth: '280px' }}
                  />
                  <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button type="button" style={styles.btnGhost} onClick={() => aplicarDiasBan(7)}>
                      +7 días
                    </button>
                    <button type="button" style={styles.btnGhost} onClick={() => aplicarDiasBan(30)}>
                      +30 días
                    </button>
                    <button type="button" style={styles.btnGhost} onClick={() => aplicarDiasBan(90)}>
                      +90 días
                    </button>
                    <button type="button" style={styles.btnGhost} onClick={() => aplicarAniosBan(1)}>
                      1 año
                    </button>
                    <button type="button" style={styles.btnGhost} onClick={() => aplicarAniosBan(4)}>
                      4 años
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', fontSize: '13px' }}>
                  Motivo del baneo
                </label>
                <textarea
                  value={banMotivo}
                  onChange={(e) => setBanMotivo(e.target.value)}
                  rows={5}
                  placeholder="Describe el motivo de la sanción..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    resize: 'vertical',
                    minHeight: '100px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                style={styles.btnGhost}
                disabled={banSubmitting}
                onClick={() => setBanModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                style={{ ...styles.btnPrimary, background: '#b71c1c' }}
                disabled={banSubmitting}
                onClick={handleConfirmarBanear}
              >
                {banSubmitting ? 'Procesando...' : 'Confirmar baneo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR DATOS PERSONALES */}
      {usuarioEditDatos && (
        <div style={styles.modalOverlay}>
          <div style={{
            ...styles.modal,
            width: '440px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <div style={{ flexShrink: 0 }}>
              <h3 style={{ marginTop: 0 }}>Editar datos personales</h3>
              <p style={{ color: '#666', marginBottom: 0, fontSize: '14px' }}>
                {usuarioEditDatos.rut}
              </p>
            </div>
            <div style={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              marginTop: '16px',
            }}>
              <div style={{ display: 'grid', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '13px' }}>Nombre</label>
                  <input
                    type="text"
                    value={editDatosForm.nombre || ''}
                    onChange={e => setEditDatosForm(prev => ({ ...prev, nombre: e.target.value }))}
                    style={{ ...styles.input, width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '13px' }}>Apellido</label>
                  <input
                    type="text"
                    value={editDatosForm.apellido || ''}
                    onChange={e => setEditDatosForm(prev => ({ ...prev, apellido: e.target.value }))}
                    style={{ ...styles.input, width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '13px' }}>Email</label>
                  <input
                    type="email"
                    value={editDatosForm.email || ''}
                    onChange={e => setEditDatosForm(prev => ({ ...prev, email: e.target.value }))}
                    style={{ ...styles.input, width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '13px' }}>Teléfono</label>
                  <input
                    type="text"
                    value={editDatosForm.telefono || ''}
                    onChange={e => setEditDatosForm(prev => ({ ...prev, telefono: e.target.value }))}
                    style={{ ...styles.input, width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '13px' }}>Fecha nacimiento</label>
                  <input
                    type="date"
                    value={editDatosForm.fecha_nacimiento || ''}
                    onChange={e => setEditDatosForm(prev => ({ ...prev, fecha_nacimiento: e.target.value }))}
                    style={{ ...styles.input, width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '13px' }}>Género</label>
                  <select
                    value={editDatosForm.genero || ''}
                    onChange={e => setEditDatosForm(prev => ({ ...prev, genero: e.target.value }))}
                    style={{ ...styles.select, width: '100%' }}
                  >
                    <option value="">—</option>
                    <option value="M">M</option>
                    <option value="F">F</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '13px' }}>Ciudad</label>
                  <input
                    type="text"
                    value={editDatosForm.ciudad || ''}
                    onChange={e => setEditDatosForm(prev => ({ ...prev, ciudad: e.target.value }))}
                    style={{ ...styles.input, width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '13px' }}>Región</label>
                  <input
                    type="text"
                    value={editDatosForm.region || ''}
                    onChange={e => setEditDatosForm(prev => ({ ...prev, region: e.target.value }))}
                    style={{ ...styles.input, width: '100%' }}
                  />
                </div>
              </div>
            </div>
            <div style={{ ...styles.modalFooter, flexShrink: 0 }}>
              <button style={styles.btnGhost} onClick={() => { setUsuarioEditDatos(null); setUsuarioVer(null); }}>Volver a usuarios</button>
              <button style={styles.btnPrimary} onClick={guardarEdicionDatos}>Guardar cambios</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR USUARIO (estado + perfiles) */}
      {usuarioConfig && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, width: '420px' }}>
            <h3 style={{ marginTop: 0 }}>Editar usuario</h3>
            <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
              {usuarioConfig.nombre} {usuarioConfig.apellido} ({usuarioConfig.rut})
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>Estado</label>
              <select
                value={nuevoEstado}
                onChange={e => setNuevoEstado(e.target.value)}
                style={{ ...styles.select, width: '100%' }}
              >
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
                <option value="baneado">Baneado</option>
                <option value="suspendido">Suspendido</option>
              </select>
            </div>

            <div style={{
              marginBottom: '16px',
              padding: '14px 16px',
              background: '#f8f9fa',
              borderRadius: '10px',
              border: '1px solid #eee',
            }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#37474f' }}>Perfiles</label>
              <p style={{ color: '#666', fontSize: '12px', marginBottom: '12px' }}>
                El usuario puede tener varios perfiles a la vez.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {TIPOS_PERFIL.map(({ value, label }) => {
                  const selected = perfilesSeleccionados.includes(value);
                  const c = perfilColor[value] || perfilColorDefault;
                  return (
                    <label
                      key={value}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 14px',
                        background: selected ? c.bg : '#fff',
                        border: `2px solid ${selected ? c.border : '#e0e0e0'}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: selected ? 600 : 500,
                        color: selected ? c.color : '#616161',
                        boxShadow: selected ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => togglePerfil(value)}
                        style={{ width: '16px', height: '16px', accentColor: c.border }}
                      />
                      {label}
                    </label>
                  );
                })}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.btnGhost}
                onClick={() => setUsuarioConfig(null)}
              >
                Cancelar
              </button>
              <button
                style={styles.btnPrimary}
                onClick={guardarEdicion}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsuariosView;
